import { useCallback } from 'react'
import type { ComboVariation } from '@/types'
import type { LayoutConfig } from '@/utils/dealCalculations'
import { formatCategoryDisplayTitle } from '@/utils/text-normalization'
import { renameCategoryCoordKey } from '@/utils/planCatalog'
import { recurringSetupEntry } from '@/utils/recurringSetupEntry'
import type { StudioActionDeps } from './proposalStudioActionTypes'
import { getActiveComboVariationIndex } from './computeActivePlanContext'

export function useProposalStudioPlanEditActions(deps: StudioActionDeps) {
  const {
    proposalItems,
    activeIndex,
    variationsWithPricing,
    setProposalItems,
    setLayout,
    setComboVariations,
    scopeLayoutSignatureRef,
  } = deps

  const handleSliderChange = useCallback((key: keyof LayoutConfig, value: number) => {
    setLayout((prev) => {
      const next = { ...prev, [key]: value }
      if (key === 'leftBlockWidth') {
        next.dividerX = value
      }
      return next
    })
  }, [setLayout])

  const handleCategoryCoordChange = useCallback(
    (category: string, axis: 'x' | 'y', value: number) => {
      setLayout((prev) => {
        const categoryCoords = { ...(prev.categoryCoords || {}) }
        const current = categoryCoords[category] || {
          x: prev.serviceBlockX || 38,
          y: prev.serviceBlockStartY || 10,
        }
        categoryCoords[category] = { ...current, [axis]: value }
        return { ...prev, categoryCoords }
      })
    },
    [setLayout],
  )

  const handleUpdateActiveVariationField = useCallback(
    (field: keyof ComboVariation, value: unknown) => {
      const variationIdx = getActiveComboVariationIndex(variationsWithPricing, activeIndex)
      setComboVariations((prev) =>
        prev.map((v, idx) => (idx !== variationIdx ? v : { ...v, [field]: value })),
      )
    },
    [activeIndex, variationsWithPricing, setComboVariations],
  )

  const handleUpdateActivePlanField = useCallback(
    (field: string, value: unknown) => {
      setProposalItems((prev) =>
        prev.map((item, idx) => {
          if (idx !== activeIndex) return item
          const updated = { ...item, [field]: value }
          if (field === 'descricao') {
            updated.nome = value as string
            updated.name = value as string
          }
          if (field === 'duration') {
            const dur = Number(value) || 12
            if (updated.pricing) {
              const entry = updated.pricing.entry || 0
              const instVal = updated.pricing.installmentValue || 0
              updated.pricing = {
                ...updated.pricing,
                recorrente: true,
                installments: dur,
                total: entry + dur * instVal,
              }
              updated.total = (entry + dur * instVal) * (updated.quantidade || 1)
            }
          }
          return updated
        }),
      )
    },
    [activeIndex, setProposalItems],
  )

  const handleUpdateActivePlanPricing = useCallback(
    (updates: Record<string, unknown>) => {
      setProposalItems((prev) =>
        prev.map((item, idx) => {
          if (idx !== activeIndex) return item
          const nextPricing = {
            ...(item.pricing || {
              total: item.valorUnitario * (item.duration || 12),
              entry: 0,
              installments: item.duration || 12,
              installmentValue: item.valorUnitario,
              recorrente: item.tipo === 'plano',
            }),
            ...updates,
          }

          const entryTouched = Object.prototype.hasOwnProperty.call(updates, 'entry')
          const entryNum = entryTouched
            ? Number(nextPricing.entry || 0)
            : recurringSetupEntry({ ...nextPricing, recorrente: true })
          nextPricing.entry = entryNum
          const installmentValueNum = Number(nextPricing.installmentValue || 0)
          nextPricing.recorrente = true
          nextPricing.total = entryNum + Number(item.duration || 12) * installmentValueNum
          nextPricing.installments = Number(item.duration || 12)
          return {
            ...item,
            pricing: nextPricing,
            valorUnitario: nextPricing.installmentValue,
            total: nextPricing.total * item.quantidade,
          }
        }),
      )
    },
    [activeIndex, setProposalItems],
  )

  const handleRenameCategory = useCallback(
    (catIndex: number, newName: string) => {
      const trimmed = formatCategoryDisplayTitle(newName)
      if (!trimmed) return
      const item = proposalItems[activeIndex]
      const cat = item?.services?.[catIndex]
      if (!cat) return
      const oldName = String(cat.category || cat.categoria || '')
      setProposalItems((prev) =>
        prev.map((row, idx) => {
          if (idx !== activeIndex) return row
          const servicesList = [...(row.services || [])]
          servicesList[catIndex] = { ...cat, category: trimmed, categoria: trimmed }
          return { ...row, services: servicesList }
        }),
      )
      setLayout((layoutPrev) => ({
        ...layoutPrev,
        categoryCoords: renameCategoryCoordKey(layoutPrev.categoryCoords, oldName, trimmed),
      }))
      scopeLayoutSignatureRef.current = ''
    },
    [proposalItems, activeIndex, setProposalItems, setLayout, scopeLayoutSignatureRef],
  )

  return {
    handleSliderChange,
    handleCategoryCoordChange,
    handleUpdateActiveVariationField,
    handleUpdateActivePlanField,
    handleUpdateActivePlanPricing,
    handleRenameCategory,
  }
}
